/**
 * Order messages component
 *
 * @version
 * 4.77.0 OAUI-722 Order messages: refactor to only use mongo messages (remove dependency on core-api)
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.58.0 OAUI-635 Add checks to Messages for MEAN order updating
 * 4.36.0 OAUI-518 Add order reports and exports
 * 4.36.0 OAUI-515 Add / Edit / Remove Order Messages
 * 4.35.0 OAUI-499 View Order Messages
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { WdOrderMessageService } from '../../services/orders/messages/wd-order-message.service';
import { WdLocalEnvironmentService } from '../../services/environment/wd-local-environment.service';
import { IOrderMessageModel } from '../../interfaces/orders/order-interfaces';

@Component({
  selector: 'app-order-messages',
  templateUrl: './order-messages.component.html',
  styleUrls: ['./order-messages.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrderMessagesComponent {
  // Inputs
  @Input() messages?: Array<IOrderMessageModel>;
  @Input() orderIndex = '';

  // Outputs
  @Output() messagesChanged: EventEmitter<any> = new EventEmitter();

  // New Message
  message = '';

  // Booleans
  isMessagesUpdating = false;

  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    public wdEnvironmentService: WdLocalEnvironmentService,
    private wdOrderMessageService: WdOrderMessageService,
    @Inject('wdErrorService') public wdErrorService: any,
  ) {}

  /**
   * Add Order Message
   * @since 4.36.0
   */
  async addOrderMessage(message: string) {
    if (message) {
      // Start loader
      this.isMessagesUpdating = true;
      try {
        // Update Order
        await this.wdOrderMessageService.addOrderMessage(
          this.orderIndex,
          message,
        );
        // Clear Message
        this.message = '';
        // Callback
        this.messagesChanged.emit(message);
      } finally {
        // Stop loader / Set State
        this.isMessagesUpdating = false;
      }
    }
  }

  /**
   * Delete Order Message
   * @since 4.36.0
   */
  async deleteOrderMessage(entry: any) {
    // Start loader
    this.isMessagesUpdating = true;
    try {
      // Delete Order Message
      await this.wdOrderMessageService.deleteOrderMessage(
        entry.id,
        this.orderIndex,
      );
      // Callback
      this.messagesChanged.emit();
    } finally {
      // Stop loader
      this.isMessagesUpdating = false;
    }
  }

  /**
   * Edit Order Message
   * @since 4.36.0
   */
  async editOrderMessage(entry: any) {
    // Start loader
    this.isMessagesUpdating = true;
    try {
      // Update Order
      await this.wdOrderMessageService.editOrderMessage(this.orderIndex, entry);
      // Close Editor
      entry.isEditing = false;
      // Callback
      this.messagesChanged.emit();
    } finally {
      // Stop loader
      this.isMessagesUpdating = false;
    }
  }

  /**
   * Start Edit Order Message
   * @since 4.36.0
   */
  cancelEditOrderMessage(entry: any) {
    // Start Editor
    entry.message = JSON.parse(entry.originalMessage);
    // Stop Editor
    entry.isEditing = false;
  }

  /**
   * Start Edit Order Message
   * @since 4.36.0
   */
  startEditOrderMessage(entry: any) {
    // Save Message Copy
    entry.originalMessage = JSON.stringify(entry.message);
    // Start Editor
    entry.isEditing = true;
  }
}
